import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import './ImageCropper.css';

function canvasToBlob(canvas, type) {
  return new Promise((resolve) => {
    canvas.toBlob(resolve, type, 0.92);
  });
}

function buildOutputFile(blob, sourceFile, fallbackBaseName) {
  const extension = blob.type === 'image/png' ? 'png' : 'jpg';
  const baseName = sourceFile?.name?.replace(/\.[^.]+$/, '') || fallbackBaseName;
  return new File([blob], `${baseName}-cropped.${extension}`, {
    type: blob.type,
  });
}

function ImageCropper({
  imageUrl,
  sourceFile = null,
  onComplete,
  onCancel,
  instructions = 'Scroll to zoom, drag to reposition',
  cropWidth = 280,
  cropHeight = 280,
  outputWidth = cropWidth,
  outputHeight = cropHeight,
  shape = 'circle',
  initialFit = 'cover',
}) {
  const canvasRef = useRef(null);
  const imageRef = useRef(null);
  const dragStartRef = useRef({ x: 0, y: 0, panX: 0, panY: 0 });
  const touchDistanceRef = useRef(null);
  const touchPanRef = useRef(null);

  const [imgLoaded, setImgLoaded] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [fitZoom, setFitZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);

  const isCircle = shape === 'circle';
  const viewportPadding = 20;
  const viewportWidth = cropWidth + viewportPadding * 2;
  const viewportHeight = cropHeight + viewportPadding * 2;
  const cropLeft = (viewportWidth - cropWidth) / 2;
  const cropTop = (viewportHeight - cropHeight) / 2;

  const clampPan = useCallback((x, y, nextZoom = zoom) => {
    if (!imageRef.current) {
      return { x, y };
    }

    const drawWidth = imageRef.current.width * nextZoom;
    const drawHeight = imageRef.current.height * nextZoom;
    const maxX = Math.max((drawWidth - cropWidth) / 2, 0);
    const maxY = Math.max((drawHeight - cropHeight) / 2, 0);

    return {
      x: Math.max(-maxX, Math.min(maxX, x)),
      y: Math.max(-maxY, Math.min(maxY, y)),
    };
  }, [cropHeight, cropWidth, zoom]);

  useEffect(() => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => {
      imageRef.current = image;
      const nextFitZoom = initialFit === 'contain'
        ? Math.min(cropWidth / image.width, cropHeight / image.height)
        : Math.max(cropWidth / image.width, cropHeight / image.height);
      setFitZoom(nextFitZoom);
      setZoom(nextFitZoom);
      setPan({ x: 0, y: 0 });
      setImgLoaded(true);
    };
    image.src = imageUrl;
  }, [cropHeight, cropWidth, imageUrl, initialFit]);

  useEffect(() => {
    if (!imgLoaded || !canvasRef.current || !imageRef.current) {
      return;
    }

    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    if (!context) {
      return;
    }

    canvas.width = viewportWidth;
    canvas.height = viewportHeight;
    context.clearRect(0, 0, viewportWidth, viewportHeight);

    const image = imageRef.current;
    const drawWidth = image.width * zoom;
    const drawHeight = image.height * zoom;
    const drawX = (viewportWidth - drawWidth) / 2 + pan.x;
    const drawY = (viewportHeight - drawHeight) / 2 + pan.y;

    context.drawImage(image, drawX, drawY, drawWidth, drawHeight);
  }, [imgLoaded, pan.x, pan.y, viewportHeight, viewportWidth, zoom]);

  const zoomBounds = useMemo(() => ({
    min: Math.max(fitZoom * 0.85, 0.1),
    max: Math.max(fitZoom * 6, fitZoom),
  }), [fitZoom]);

  const handleMouseDown = useCallback((event) => {
    event.preventDefault();
    setDragging(true);
    dragStartRef.current = {
      x: event.clientX,
      y: event.clientY,
      panX: pan.x,
      panY: pan.y,
    };
  }, [pan.x, pan.y]);

  const handleMouseMove = useCallback((event) => {
    if (!dragging) return;
    setPan(
      clampPan(
        dragStartRef.current.panX + (event.clientX - dragStartRef.current.x),
        dragStartRef.current.panY + (event.clientY - dragStartRef.current.y)
      )
    );
  }, [clampPan, dragging]);

  const handleMouseUp = useCallback(() => {
    setDragging(false);
  }, []);

  const handleTouchStart = useCallback((event) => {
    if (event.touches.length === 2) {
      const dx = event.touches[0].clientX - event.touches[1].clientX;
      const dy = event.touches[0].clientY - event.touches[1].clientY;
      touchDistanceRef.current = Math.hypot(dx, dy);
      touchPanRef.current = null;
      return;
    }

    const touch = event.touches[0];
    if (!touch) return;

    touchPanRef.current = {
      x: touch.clientX,
      y: touch.clientY,
      panX: pan.x,
      panY: pan.y,
    };
    setDragging(true);
  }, [pan.x, pan.y]);

  const handleTouchMove = useCallback((event) => {
    event.preventDefault();

    if (event.touches.length === 2 && touchDistanceRef.current !== null) {
      const dx = event.touches[0].clientX - event.touches[1].clientX;
      const dy = event.touches[0].clientY - event.touches[1].clientY;
      const nextDistance = Math.hypot(dx, dy);
      const scale = nextDistance / touchDistanceRef.current;
      touchDistanceRef.current = nextDistance;

      setZoom((currentZoom) => {
        const nextZoom = Math.max(
          zoomBounds.min,
          Math.min(currentZoom * scale, zoomBounds.max)
        );
        setPan((currentPan) => clampPan(currentPan.x, currentPan.y, nextZoom));
        return nextZoom;
      });

      return;
    }

    const touch = event.touches[0];
    if (!touch || !touchPanRef.current) return;

    setPan(
      clampPan(
        touchPanRef.current.panX + (touch.clientX - touchPanRef.current.x),
        touchPanRef.current.panY + (touch.clientY - touchPanRef.current.y)
      )
    );
  }, [clampPan, zoomBounds.max, zoomBounds.min]);

  const handleTouchEnd = useCallback(() => {
    touchDistanceRef.current = null;
    touchPanRef.current = null;
    setDragging(false);
  }, []);

  const handleWheel = useCallback((event) => {
    event.preventDefault();
    setZoom((currentZoom) => {
      const factor = event.deltaY > 0 ? 0.95 : 1.05;
      const nextZoom = Math.max(zoomBounds.min, Math.min(currentZoom * factor, zoomBounds.max));
      setPan((currentPan) => clampPan(currentPan.x, currentPan.y, nextZoom));
      return nextZoom;
    });
  }, [clampPan, zoomBounds.max, zoomBounds.min]);

  const handleSliderChange = useCallback((event) => {
    const nextZoom = parseFloat(event.target.value);
    setZoom(nextZoom);
    setPan((currentPan) => clampPan(currentPan.x, currentPan.y, nextZoom));
  }, [clampPan]);

  const handleDone = useCallback(async () => {
    if (!imageRef.current) {
      await onComplete(sourceFile);
      return;
    }

    const image = imageRef.current;
    const outputCanvas = document.createElement('canvas');
    outputCanvas.width = outputWidth;
    outputCanvas.height = outputHeight;
    const context = outputCanvas.getContext('2d');

    if (!context) {
      await onComplete(sourceFile);
      return;
    }

    const drawWidth = image.width * zoom;
    const drawHeight = image.height * zoom;
    const drawX = (viewportWidth - drawWidth) / 2 + pan.x;
    const drawY = (viewportHeight - drawHeight) / 2 + pan.y;

    const sourceX = Math.max(0, (cropLeft - drawX) / zoom);
    const sourceY = Math.max(0, (cropTop - drawY) / zoom);
    const sourceWidth = Math.min(image.width, cropWidth / zoom);
    const sourceHeight = Math.min(image.height, cropHeight / zoom);

    const usePng = isCircle || sourceFile?.type === 'image/png';

    if (isCircle) {
      context.beginPath();
      context.arc(outputWidth / 2, outputHeight / 2, Math.min(outputWidth, outputHeight) / 2, 0, Math.PI * 2);
      context.closePath();
      context.clip();
    }

    context.drawImage(
      image,
      sourceX,
      sourceY,
      sourceWidth,
      sourceHeight,
      0,
      0,
      outputWidth,
      outputHeight
    );

    const blob = await canvasToBlob(outputCanvas, usePng ? 'image/png' : 'image/jpeg');

    if (!blob) {
      await onComplete(sourceFile);
      return;
    }

    const croppedFile = buildOutputFile(
      blob,
      sourceFile,
      isCircle ? 'profile-picture' : 'banner-image'
    );

    await onComplete(croppedFile);
  }, [
    cropHeight,
    cropLeft,
    cropTop,
    cropWidth,
    isCircle,
    onComplete,
    outputHeight,
    outputWidth,
    pan.x,
    pan.y,
    sourceFile,
    viewportHeight,
    viewportWidth,
    zoom,
  ]);

  return (
    <div className="image-cropper">
      <div className="image-cropper__instructions">{instructions}</div>

      <div
        className="image-cropper__viewport"
        style={{ width: viewportWidth, height: viewportHeight }}
      >
        <canvas
          ref={canvasRef}
          className="image-cropper__canvas"
          style={{
            width: viewportWidth,
            height: viewportHeight,
            cursor: dragging ? 'grabbing' : 'grab',
          }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onWheel={handleWheel}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        />

        <div className="image-cropper__overlay">
          <svg width={viewportWidth} height={viewportHeight} className="image-cropper__mask-svg">
            <defs>
              <mask id={`crop-mask-${shape}`}>
                <rect width="100%" height="100%" fill="white" />
                {isCircle ? (
                  <circle
                    cx={viewportWidth / 2}
                    cy={viewportHeight / 2}
                    r={cropWidth / 2}
                    fill="black"
                  />
                ) : (
                  <rect
                    x={cropLeft}
                    y={cropTop}
                    width={cropWidth}
                    height={cropHeight}
                    rx="10"
                    fill="black"
                  />
                )}
              </mask>
            </defs>

            <rect
              width="100%"
              height="100%"
              fill="rgba(0,0,0,0.62)"
              mask={`url(#crop-mask-${shape})`}
            />

            {isCircle ? (
              <circle
                cx={viewportWidth / 2}
                cy={viewportHeight / 2}
                r={cropWidth / 2}
                fill="none"
                stroke="rgba(90, 178, 232, 0.9)"
                strokeWidth="2"
              />
            ) : (
              <rect
                x={cropLeft}
                y={cropTop}
                width={cropWidth}
                height={cropHeight}
                rx="10"
                fill="none"
                stroke="rgba(90, 178, 232, 0.9)"
                strokeWidth="2"
              />
            )}
          </svg>
        </div>
      </div>

      <div className="image-cropper__zoom-row">
        <span className="image-cropper__zoom-icon image-cropper__zoom-icon--small" aria-hidden="true">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
            <line x1="8" y1="11" x2="14" y2="11" />
          </svg>
        </span>
        <input
          type="range"
          className="image-cropper__slider"
          min={zoomBounds.min}
          max={zoomBounds.max}
          step="0.01"
          value={zoom}
          onChange={handleSliderChange}
        />
        <span className="image-cropper__zoom-icon image-cropper__zoom-icon--large" aria-hidden="true">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
            <line x1="11" y1="8" x2="11" y2="14" />
            <line x1="8" y1="11" x2="14" y2="11" />
          </svg>
        </span>
      </div>

      <div className="image-cropper__actions">
        <button type="button" className="image-cropper__cancel" onClick={onCancel}>
          Cancel
        </button>
        <button type="button" className="image-cropper__done" onClick={handleDone}>
          Apply
        </button>
      </div>
    </div>
  );
}

ImageCropper.propTypes = {
  imageUrl: PropTypes.string.isRequired,
  sourceFile: PropTypes.instanceOf(File),
  onComplete: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  instructions: PropTypes.string,
  cropWidth: PropTypes.number,
  cropHeight: PropTypes.number,
  outputWidth: PropTypes.number,
  outputHeight: PropTypes.number,
  shape: PropTypes.oneOf(['circle', 'banner']),
  initialFit: PropTypes.oneOf(['cover', 'contain']),
};

export default ImageCropper;
